import React, { createContext, useCallback, useContext, useState } from 'react';
import { Snackbar } from '@mui/material';
import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';

interface SnackbarContextType {
  showSnackbar: (message: string, severity: AlertColor, messageDetails?: string) => void;
}

interface SnackbarMessage {
  message: string;
  messageDetails?: string;
  severity: AlertColor;
  key: number;
}

const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => {
    return;
  },
});

export const useSnackbar = () => useContext(SnackbarContext);

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />;
});

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<SnackbarMessage | undefined>(undefined);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (snackPack.length && !messageInfo) {
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      setOpen(false);
    }
  }, [snackPack, messageInfo, open]);

  const showSnackbar = useCallback((message: string, severity: AlertColor, messageDetails?: string) => {
    setSnackPack((prev) => [...prev, { message, messageDetails, severity, key: new Date().getTime() }]);
  }, []);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    setMessageInfo(undefined);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        key={messageInfo ? messageInfo.key : undefined}
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleClose} severity={messageInfo?.severity || 'success'}>
          {messageInfo ? t(messageInfo.message, { details: messageInfo.messageDetails }) : ''}{' '}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
